// src/components/Admin/CommentManager/CommentsTable.jsx

import { useState, useMemo, useEffect } from 'react';
import {
  Box, TextField, Table, TableHead, TableBody, TableCell, TableRow,
  Button, Stack, MenuItem, Select, Typography, TableContainer, Paper,
  Pagination, IconButton, Tooltip, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReasonModal from './ReasonModal';
import { useUser } from '../../../contexts/UserContext';
import * as XLSX from 'xlsx';

const ROWS_PER_PAGE = 15;

export default function CommentsTable({
  comments = [],
  onSoftDelete,
  onHardDelete,
  onToggleBlind,
}) {
  const { userInfo } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // ✅ 모바일(sm 이하)

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => setPage(1), [search, statusFilter]);

  // 🔍 필터링
  const filteredComments = useMemo(() => (
    comments
      .filter(c => {
        if (statusFilter === 'visible') return !c.is_deleted && !c.is_hidden;
        if (statusFilter === 'hidden') return c.is_hidden;
        if (statusFilter === 'deleted') return c.is_deleted;
        return true;
      })
      .filter(c =>
        (c.content || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.username || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.post_title || '').toLowerCase().includes(search.toLowerCase())
      )
  ), [comments, search, statusFilter]);

  const totalPages = Math.ceil(filteredComments.length / ROWS_PER_PAGE) || 1;
  const paginated = filteredComments.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE
  );

  // ───────── 엑셀 내보내기 ─────────
  const handleExportExcel = () => {
    const rows = filteredComments.map((c) => ({
      ID: c.id,
      댓글내용: c.content,
      게시글제목: c.post_title,
      게시글ID: c.post_id,
      작성자: c.username,
      상태: c.is_deleted ? '삭제' : (c.is_hidden ? '블라인드' : '공개'),
    }));

    if (!rows.length) return alert('엑셀로 내보낼 댓글이 없습니다.');

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Comments');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `comments_${today}.xlsx`);
  };

  // ───────── 사유 모달 ─────────
  const openModal = (id, type, isHidden = null) => {
    setPendingAction({ id, type, isHidden });
    setModalOpen(true);
  };

  const handleAction = (memo) => {
    if (!pendingAction) return;
    const { id, type, isHidden } = pendingAction;
    setModalOpen(false);

    if (type === 'soft_delete') onSoftDelete?.(id, memo);
    else if (type === 'hard_delete') onHardDelete?.(id, memo);
    else if (type === 'blind_toggle') onToggleBlind?.(id, isHidden, memo);
  };

  const iconSx = { p: 0.25, width: 28, height: 28, lineHeight: 1 };

  // ─────────────────────────────────────────────
  // ✅ 모바일: 카드 리스트 (가로스크롤 0, 한 화면에 자연스럽게)
  // ─────────────────────────────────────────────
  const MobileList = () => (
    <Stack spacing={1.25}>
      {paginated.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          🔍 조건에 맞는 댓글이 없습니다.
        </Paper>
      ) : (
        paginated.map((c) => {
          const stateLabel = c.is_deleted ? '삭제' : (c.is_hidden ? '블라인드' : '공개');

          const contentText = c.is_deleted
            ? '삭제된 댓글입니다'
            : c.is_hidden
              ? '블라인드된 댓글입니다'
              : (c.content || '');

          return (
            <Paper key={c.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              {/* 상단: 상태 / 작성자 */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {stateLabel}
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  {c.username}
                </Typography>
              </Stack>

              {/* 게시글 */}
              <Typography
                variant="body2"
                sx={{ mt: 0.5, fontWeight: 700 }}
                noWrap
              >
                <a
                  href={`/main/posts/${c.post_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: '#1976d2' }}
                >
                  {c.post_title || `게시글 #${c.post_id}`}
                </a>
              </Typography>

              {/* 댓글 내용 */}
              <Typography
                variant="body2"
                sx={{ mt: 0.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {contentText}
              </Typography>

              {/* 액션 */}
              <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                {!c.is_deleted && (
                  <>
                    <Tooltip title={c.is_hidden ? '숨김 해제' : '블라인드'}>
                      <IconButton
                        size="small"
                        sx={iconSx}
                        color={c.is_hidden ? 'primary' : 'secondary'}
                        onClick={() => openModal(c.id, 'blind_toggle', !c.is_hidden)}
                      >
                        {c.is_hidden
                          ? <VisibilityIcon sx={{ fontSize: 18 }} />
                          : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="삭제">
                      <IconButton
                        size="small"
                        sx={iconSx}
                        color="error"
                        onClick={() => openModal(c.id, 'soft_delete')}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}

                {userInfo?.role === 'master' && (
                  <Tooltip title="영구삭제">
                    <IconButton
                      size="small"
                      sx={iconSx}
                      color="warning"
                      onClick={() => openModal(c.id, 'hard_delete')}
                    >
                      <DeleteForeverIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Paper>
          );
        })
      )}
    </Stack>
  );

  // ─────────────────────────────────────────────
  // ✅ PC: 기존 테이블 유지
  // ─────────────────────────────────────────────
  const DesktopTable = () => (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 2,
        width: '100%',
        overflowX: 'hidden', // ✅ PC는 가로스크롤 필요 없게(원하면 auto로)
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          width: '100%',
          '& td, & th': {
            borderBottom: '1px solid #eee',
            whiteSpace: 'nowrap',
            p: 1.25,
          },
          '& th': { bgcolor: 'grey.50', fontWeight: 600 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>댓글내용</TableCell>
            <TableCell>게시글</TableCell>
            <TableCell>작성자</TableCell>
            <TableCell align="center">상태</TableCell>
            <TableCell align="center" sx={{ width: 140 }}>관리</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginated.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                🔍 조건에 맞는 댓글이 없습니다.
              </TableCell>
            </TableRow>
          ) : (
            paginated.map((comment) => {
              const stateLabel = comment.is_deleted
                ? '삭제'
                : comment.is_hidden ? '블라인드' : '공개';

              const actions = (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {!comment.is_deleted && (
                    <>
                      <Button
                        size="small"
                        color={comment.is_hidden ? 'primary' : 'secondary'}
                        onClick={() => openModal(comment.id, 'blind_toggle', !comment.is_hidden)}
                      >
                        {comment.is_hidden ? '해제' : '블라인드'}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => openModal(comment.id, 'soft_delete')}
                      >
                        삭제
                      </Button>
                    </>
                  )}
                  {userInfo?.role === 'master' && (
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => openModal(comment.id, 'hard_delete')}
                    >
                      영구삭제
                    </Button>
                  )}
                </Stack>
              );

              return (
                <TableRow key={comment.id} hover sx={{ '&:hover td': { bgcolor: 'grey.100' } }}>
                  <TableCell>
                    {comment.is_deleted
                      ? '삭제된 댓글입니다'
                      : comment.is_hidden
                        ? '블라인드된 댓글입니다'
                        : comment.content}
                  </TableCell>

                  <TableCell>
                    <a
                      href={`/main/posts/${comment.post_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: '#1976d2' }}
                    >
                      {comment.post_title || `게시글 #${comment.post_id}`}
                    </a>
                  </TableCell>

                  <TableCell>{comment.username}</TableCell>
                  <TableCell align="center">{stateLabel}</TableCell>
                  <TableCell align="center">{actions}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: isMobile ? 1.5 : 2, width: '100%', minWidth: 0 }}>
      {/* 🔍 검색·필터 */}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={1.5}
        sx={{ mb: 2, alignItems: isMobile ? 'stretch' : 'center' }}
      >
        <TextField
          label="댓글/게시글/작성자"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          fullWidth={isMobile}
          sx={{ minWidth: isMobile ? '100%' : 240 }}
        />

        <Select
          size="small"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          fullWidth={isMobile}
          sx={{ minWidth: isMobile ? '100%' : 140 }}
        >
          <MenuItem value="all">전체</MenuItem>
          <MenuItem value="visible">공개</MenuItem>
          <MenuItem value="hidden">블라인드</MenuItem>
          <MenuItem value="deleted">삭제</MenuItem>
        </Select>

        <Typography variant="body2" color="text.secondary">
          총 {comments.length}개 / 현재 {filteredComments.length}개
        </Typography>

        <Button
          variant="outlined"
          size="small"
          onClick={handleExportExcel}
          fullWidth={isMobile}
        >
          📥 엑셀로 내보내기
        </Button>
      </Stack>

      {/* ✅ 본문 */}
      {isMobile ? <MobileList /> : <DesktopTable />}

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            shape="rounded"
            color="primary"
            size={isMobile ? 'small' : 'medium'}
          />
        </Box>
      )}

      {/* 사유 입력 모달 */}
      <ReasonModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleAction}
        actionLabel={
          pendingAction?.type === 'soft_delete' ? '삭제'
            : pendingAction?.type === 'hard_delete' ? '영구삭제'
              : pendingAction?.type === 'blind_toggle'
                ? (pendingAction?.isHidden ? '블라인드' : '해제')
                : '처리'
        }
      />
    </Box>
  );
}
